import React, {ReactNode} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import isAnalysisGraphQLInternallyConsistent from 'analysis-tool/data/isAnalysisGraphQLInternallyConsistent';
import usePollingAnalysis from 'analysis-tool/ui/hooks/usePollingAnalysis';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

export interface PollingAnalysisProps {
  analysisId: string;
  children: (data: AnalysisGraphQL) => ReactNode;
}

export default function PollingAnalysis(props: PollingAnalysisProps): JSX.Element {
  const result = usePollingAnalysis(props.analysisId);

  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  if (isInaccessible(result.data.analysis)) {
    return (
      <WorkspaceItemInaccessibleGuard
        inaccessibleInfo={result.data.analysis}
        modelType={ModelTypeConstants.ANALYSIS}
      />
    );
  }

  /**
   * Occasionally, when the analysis is updated in the middle of the `GetAnalysis` GraphQL response being
   * generated, the GraphQL response may be internally inconsistent, until the next re-fetch.
   *
   * To mitigate user impact while we investigate the underlying cause of these inconsistencies, render
   * the previous data and refetch.
   */
  if (!isAnalysisGraphQLInternallyConsistent(result.data.analysis)) {
    setTimeout(() => {
      result.refetch();
    }, 50);

    if (
      result.previousData != null &&
      !isInaccessible(result.previousData.analysis) &&
      isAnalysisGraphQLInternallyConsistent(result.previousData.analysis)
    ) {
      return <>{props.children(result.previousData.analysis)}</>;
    }
    return <CenteredHorizontalThrobber />;
  }

  return <>{props.children(result.data.analysis)}</>;
}
